import React, { useEffect, useRef } from 'react';
import Icon from './Icon';

/** Prototype .bar. `pct` 0–100. */
export function ProgressBar({ pct = 0, height, style }) {
  const w = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div className="wz-bar" style={{ ...(height ? { height } : null), ...style }} role="presentation">
      <span style={{ width: `${w}%` }} />
    </div>
  );
}

/** Prototype .search: icon, visually-hidden label, input. */
export function SearchInput({ label, placeholder, value, onChange, width, height, style, inputProps }) {
  return (
    <label className="wz-search" style={{ ...(width ? { width } : null), ...(height ? { height } : null), ...style }}>
      <Icon name="search" />
      <span className="wz-sr">{label}</span>
      <input type="search" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} {...inputProps} />
    </label>
  );
}

/**
 * Prototype .seg. `options`: [{ value, label, count? }]. Buttons use aria-pressed,
 * as in the prototype.
 */
export function SegmentedControl({ options, value, onChange, label }) {
  return (
    <div className="wz-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
          {o.count != null && <span className="n">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Prototype .count. `warn` = orange (empty set). */
export function CountBadge({ children, warn = false, style }) {
  return <span className={`wz-count${warn ? ' warn' : ''}`} style={style}>{children}</span>;
}

/** Prototype .panel (+ optional .panel-head). */
export function Panel({ as: Tag = 'section', className = '', children, ...rest }) {
  return <Tag className={`wz-panel${className ? ` ${className}` : ''}`} {...rest}>{children}</Tag>;
}

export function PanelHead({ children, style }) {
  return <div className="wz-panel-head" style={style}>{children}</div>;
}

export const initialsOf = (name) =>
  String(name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 3).map((w) => w[0].toUpperCase()).join('') || '?';

/** Prototype .avatar: initials in a grey circle. */
export function Avatar({ name, size, style }) {
  return (
    <span className="wz-avatar" style={{ ...(size ? { width: size, height: size } : null), ...style }} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

/** Prototype kebab(): a ⋯ button. */
export const KebabButton = React.forwardRef(function KebabButton({ label, ...rest }, ref) {
  return (
    <button ref={ref} type="button" className="wz-kebab" aria-label={label} {...rest}>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
        <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
      </svg>
    </button>
  );
});

const DIFF_LEVEL = { easy: 1, medium: 2, hard: 3 };

/** Prototype diffBars(): three rising bars + label. */
export function DifficultyBars({ level }) {
  const key = String(level || 'medium').toLowerCase();
  const n = DIFF_LEVEL[key] || 2;
  const label = key.charAt(0).toUpperCase() + key.slice(1);
  return (
    <span className="wz-diff">
      <span className="bars">
        {[1, 2, 3].map((i) => <i key={i} className={i <= n ? 'on' : ''} style={{ height: 4 + i * 3 }} />)}
      </span>
      {label}
    </span>
  );
}

/** Prototype .twist: rotating chevron for expand/collapse. */
export function TwistButton({ expanded, label, onClick }) {
  return (
    <button type="button" className="wz-twist" aria-expanded={expanded} aria-label={label} onClick={onClick}>
      <Icon name="chevD" />
    </button>
  );
}

/**
 * Prototype .menu: a trigger plus a popover list. Closes on outside click and
 * Escape. `trigger` is rendered by the caller via render prop.
 */
export function Menu({ open, onClose, trigger, children, minWidth, align = 'right' }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose]);
  return (
    <div className="wz-menu" ref={ref}>
      {trigger}
      {open && (
        <div className="wz-menu-list" role="menu" style={{ ...(minWidth ? { minWidth } : null), ...(align === 'left' ? { left: 0, right: 'auto' } : null) }}>
          {children}
        </div>
      )}
    </div>
  );
}

/** A menu item with a bold title and a small description line. */
export function MenuItem({ title, hint, onClick, danger = false, disabled = false }) {
  return (
    <button type="button" role="menuitem" className={danger ? 'danger' : ''} onClick={onClick} disabled={disabled}>
      <b style={{ fontWeight: hint ? 700 : 500 }}>{title}</b>
      {hint && <small>{hint}</small>}
    </button>
  );
}
